// @oasis-permission-inherits: /basecamp (studio-edits:view)
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";

import { Button } from "~/components/ui/button";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import { queryClient } from "~/lib/query-client";
import {
  userOutputExportStatusesQuery,
  userOutputStylesQuery,
  userOutputsListParams,
  userOutputsQuery,
} from "~/modules/basecamp-user-outputs/queries";
import {
  userOutputSearchSchema,
  userOutputTierSchema,
  type UserOutput,
  type UserOutputExportStatus,
} from "~/modules/basecamp-user-outputs/schemas";
import { UserOutputRow } from "~/modules/basecamp-user-outputs/user-output-row";

export const Route = createFileRoute("/_dash/basecamp/user-outputs/")({
  validateSearch: userOutputSearchSchema,
  component: UserOutputsPage,
});

function UserOutputsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const listParams = userOutputsListParams(search);
  const styles = userOutputStylesQuery.useQuery();
  const unlistedStyleId =
    search.styleId &&
    search.styleId !== "none" &&
    !styles.data?.some((style) => style.styleId === search.styleId)
      ? search.styleId
      : null;
  const {
    data,
    error,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = userOutputsQuery.useInfiniteQuery(listParams);
  const outputs = data?.pages.flatMap((page) => page.items) ?? [];
  const [scrollTargetId, setScrollTargetId] = useState<string | null>(null);
  const clearScrollTarget = useCallback(() => setScrollTargetId(null), []);

  async function scrollPast(operationId: string) {
    function following(loaded: typeof outputs) {
      const index = loaded.findIndex((output) => output.operationId === operationId);
      return loaded.slice(index + 1).find((output) => output.videoAssetId);
    }
    // The next playable output may be on a page not loaded yet; filtered pages can be empty.
    let next = following(outputs);
    let morePages = hasNextPage;
    while (!next && morePages) {
      const fetched = await fetchNextPage();
      if (fetched.isError) {
        break;
      }
      next = following(fetched.data?.pages.flatMap((page) => page.items) ?? []);
      morePages = fetched.hasNextPage;
    }
    if (next) {
      setScrollTargetId(next.operationId);
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">User outputs</h1>
          <p className="text-sm text-muted-foreground">
            A sample of production creates, newest first. Watch, note and rate each one in place.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={isFetching}
          onClick={() => {
            void refetch();
            void userOutputExportStatusesQuery.invalidateAllQueries(queryClient);
          }}
        >
          Refresh
        </Button>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label htmlFor="user-output-style">Style</Label>
          <Select
            value={search.styleId || "any"}
            onValueChange={(styleId) =>
              void navigate({ search: { ...search, styleId: styleId === "any" ? "" : styleId } })
            }
          >
            <SelectTrigger id="user-output-style" className="w-60 max-w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any style</SelectItem>
              {styles.data?.map((style) => (
                <SelectItem key={style.styleId} value={style.styleId}>
                  {style.name}
                </SelectItem>
              ))}
              {unlistedStyleId && (
                <SelectItem value={unlistedStyleId}>{unlistedStyleId}</SelectItem>
              )}
              {styles.isLoading && (
                <SelectItem value="loading" disabled>
                  Loading styles…
                </SelectItem>
              )}
              <SelectItem value="none">No style</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="user-output-tier">Chosen tier</Label>
          <Select
            value={search.chosenTier}
            onValueChange={(chosenTier) =>
              void navigate({
                search: {
                  ...search,
                  chosenTier: userOutputSearchSchema.shape.chosenTier.parse(chosenTier),
                },
              })
            }
          >
            <SelectTrigger id="user-output-tier" className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any tier</SelectItem>
              {userOutputTierSchema.options.map((tier) => (
                <SelectItem key={tier} value={tier}>
                  {tier}
                </SelectItem>
              ))}
              <SelectItem value="none">No tier</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="user-output-employees">Employee creates</Label>
          <Select
            value={search.employees}
            onValueChange={(employees) =>
              void navigate({
                search: {
                  ...search,
                  employees: userOutputSearchSchema.shape.employees.parse(employees),
                },
              })
            }
          >
            <SelectTrigger id="user-output-employees" className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="exclude">Hide</SelectItem>
              <SelectItem value="include">Show</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {styles.error && (
        <p role="alert" className="text-sm text-destructive">
          Failed to load the style catalog: {styles.error.message}
        </p>
      )}
      {error && (
        <p
          role="alert"
          className="rounded-md border border-destructive p-4 text-sm text-destructive"
        >
          Failed to load user outputs: {error.message}
        </p>
      )}
      {isLoading && <Skeleton className="h-64 w-full" />}
      {!isLoading && !error && outputs.length === 0 && (
        <p className="rounded-md border p-8 text-center text-muted-foreground">
          No user outputs match these filters.
        </p>
      )}
      <div className="space-y-4">
        {data?.pages.map((page, index) => (
          <UserOutputPageRows
            key={index}
            outputs={page.items}
            renderRow={(output, exportStatus) => (
              <UserOutputRow
                key={output.operationId}
                output={output}
                exportStatus={exportStatus}
                listParams={listParams}
                onRated={() => void scrollPast(output.operationId)}
                isScrollTarget={output.operationId === scrollTargetId}
                onScrolledTo={clearScrollTarget}
              />
            )}
          />
        ))}
      </div>
      {hasNextPage && (
        <Button
          variant="outline"
          disabled={isFetchingNextPage}
          onClick={() => void fetchNextPage()}
        >
          {isFetchingNextPage ? "Loading…" : "Load more"}
        </Button>
      )}
    </div>
  );
}

/** One loaded page of rows, with that page's export status fetched in one request. */
function UserOutputPageRows({
  outputs,
  renderRow,
}: {
  outputs: UserOutput[];
  renderRow: (
    output: UserOutput,
    exportStatus: UserOutputExportStatus | null | undefined
  ) => React.ReactNode;
}) {
  const operationIds = outputs.map((output) => output.operationId);
  const exportStatuses = userOutputExportStatusesQuery.useQuery(
    { params: { operationIds: operationIds.join(",") } },
    { enabled: operationIds.length > 0, staleTime: Infinity }
  );
  return outputs.map((output) => renderRow(output, exportStatuses.data?.items[output.operationId]));
}
